import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const STORAGE_KEY = 'flightiq.selectedDroneName';

const DroneSelector = ({ value, onChange }) => {
  const [drones, setDrones] = useState([]);
  const [internalSelected, setInternalSelected] = useState('');
  const emittedOnceRef = useRef(false);

  // Use controlled value when provided, else internal
  const selectedName = useMemo(
    () => (typeof value === 'string' ? value : internalSelected),
    [value, internalSelected]
  );

  useEffect(() => {
    const loadDrones = async () => {
      try {
        const res = await fetch('/API/dronespecs.xml');
        const text = await res.text();
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, 'application/xml');
        const droneEls = Array.from(xml.querySelectorAll('drone'));

        const loaded = droneEls.map((el) => {
          const name = el.getAttribute('name');
          const mfg = el.closest('manufacturer')?.getAttribute('name') ?? 'Unknown';
          return {
            name,
            manufacturer: mfg,
            maxWindTolerance: el.querySelector('maxWindTolerance')?.textContent ?? null,
            maxSpeed:        el.querySelector('maxSpeed')?.textContent ?? null,
            maxRange:        el.querySelector('maxRange')?.textContent ?? null,
            maxFlightTime:   el.querySelector('maxFlightTime')?.textContent ?? null,
            canFlyInRain:    el.querySelector('canFlyInRain')?.textContent ?? 'No',
            notes:           el.querySelector('notes')?.textContent ?? ''
          };
        });

        setDrones(loaded);

        // Restore last choice in uncontrolled mode
        if (typeof value !== 'string') {
          const saved = localStorage.getItem(STORAGE_KEY) || '';
          if (saved && loaded.some(d => d.name === saved)) {
            setInternalSelected(saved);
            if (onChange && !emittedOnceRef.current) {
              onChange(loaded.find(d => d.name === saved) || null);
              emittedOnceRef.current = true;
            }
          }
        }
      } catch (err) {
        console.error('Failed to load /API/dronespecs.xml', err);
      }
    };

    loadDrones();
  }, [value, onChange]);

  // Keep internal mirror in sync when parent controls value
  useEffect(() => {
    if (typeof value === 'string') setInternalSelected(value);
  }, [value]);

  const handleChange = (e) => {
    const name = e.target.value;
    setInternalSelected(name);
    localStorage.setItem(STORAGE_KEY, name);

    const obj = drones.find(d => d.name === name) || null;
    onChange?.(obj);
  };

  return (
    <FormControl variant="outlined" size="small" sx={{ minWidth: 200, maxWidth: 300 }}>
      <InputLabel id="drone-select-label">Drone</InputLabel>
      <Select
        labelId="drone-select-label"
        label="Drone"
        value={selectedName}
        onChange={handleChange}
      >
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
        {drones.map((drone) => (
          <MenuItem key={drone.name} value={drone.name}>
            {drone.manufacturer} – {drone.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default DroneSelector;
